// launch-check.tsx — the animated blue check shown on the post-launch
// confirmation screen (launched.tsx).
//
// WHY THIS IS PLAIN SVG + CSS (not a Lottie):
// The previous version rendered `check-animation.json` through lottie-react. On
// the WEBSITE that works, but inside the EXTENSION the launched screen lives in a
// shadow-DOM overlay where lottie-web builds the <svg> skeleton but never fills
// the path geometry — the rendered paths come out EMPTY (`<path></path>` with no
// `d`/`fill`), so the box is blank. Neither the canvas renderer nor forcing
// playback fixed it (empty paths mean the frame is never drawn at all). Since the
// check is a decorative confirmation, we render it as a self-contained SVG that
// draws itself in with CSS keyframes — no animation engine, so it renders
// identically and reliably in the extension overlay AND the website.
//
// The look mirrors the original asset: a soft light-blue disc, a blue tick that
// draws in, and a scatter of small sparkle marks that pop around it. Keyframes
// live in styles/campaigns-redesign.css (`.bt-launch-check …`).

import React from 'react';

export interface LaunchCheckProps {
  /** Rendered box size in px (square). Default 120. */
  size?: number;
}

// Sparkle marks scattered around the disc. `d` is drawn in a 120×120 viewBox;
// `t` picks the mark shape; `s` staggers the pop-in.
const SPARKLES: { x: number; y: number; kind: 'plus' | 'cross' | 'ring' | 'dot'; delay: number }[] = [
  { x: 30, y: 30, kind: 'plus', delay: 0.30 },
  { x: 92, y: 34, kind: 'ring', delay: 0.42 },
  { x: 26, y: 66, kind: 'cross', delay: 0.48 },
  { x: 96, y: 70, kind: 'dot', delay: 0.36 },
  { x: 44, y: 22, kind: 'ring', delay: 0.54 },
  { x: 82, y: 92, kind: 'plus', delay: 0.50 },
  { x: 40, y: 96, kind: 'dot', delay: 0.44 },
  { x: 74, y: 20, kind: 'cross', delay: 0.58 },
];

function Sparkle({ x, y, kind, delay }: (typeof SPARKLES)[number]) {
  const common = { stroke: '#8fc4f2', strokeWidth: 2, strokeLinecap: 'round' as const, fill: 'none' };
  const body =
    kind === 'plus' ? (
      <path d={`M${x - 3},${y} L${x + 3},${y} M${x},${y - 3} L${x},${y + 3}`} {...common} />
    ) : kind === 'cross' ? (
      <path d={`M${x - 2.5},${y - 2.5} L${x + 2.5},${y + 2.5} M${x + 2.5},${y - 2.5} L${x - 2.5},${y + 2.5}`} {...common} />
    ) : kind === 'ring' ? (
      <circle cx={x} cy={y} r={2.6} {...common} />
    ) : (
      <circle cx={x} cy={y} r={1.8} fill="#8fc4f2" />
    );
  return (
    <g className="bt-launch-check__sparkle" style={{ animationDelay: `${delay}s`, transformOrigin: `${x}px ${y}px` }}>
      {body}
    </g>
  );
}

export const LaunchCheck: React.FC<LaunchCheckProps> = ({ size = 120 }) => (
  <svg
    className="bt-launch-check"
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Soft disc — outer glow ring + inner fill, scale-popped in. */}
    <g className="bt-launch-check__disc" style={{ transformOrigin: '60px 60px' }}>
      <circle cx="60" cy="60" r="32" fill="#e8f4ff" />
      <circle cx="60" cy="60" r="25" fill="#cfe7fd" />
    </g>
    {/* The tick, drawn in via stroke-dashoffset. */}
    <path
      className="bt-launch-check__tick"
      d="M48 61 L56 70 L74 50"
      stroke="#1f81c4"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    {SPARKLES.map((s, i) => (
      <Sparkle key={i} {...s} />
    ))}
  </svg>
);

export default LaunchCheck;
